import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Instagram, Facebook, Youtube, Menu, X } from "lucide-react";
import { TikTokIcon } from "@/components/icons";

const NAV_LINKS = [
  { to: "/", label: "Home", testId: "nav-link-home" },
  { to: "/support", label: "Support", testId: "nav-link-support" },
  { to: "/privacy", label: "Privacy", testId: "nav-link-privacy" },
];

const SOCIALS = [
  { href: "https://www.instagram.com/yesamen.app/", label: "Instagram", Icon: Instagram, testId: "nav-social-instagram" },
  { href: "https://www.facebook.com/yesamenapp/", label: "Facebook", Icon: Facebook, testId: "nav-social-facebook" },
  { href: "https://www.youtube.com/@yesamenapp", label: "YouTube", Icon: Youtube, testId: "nav-social-youtube" },
  { href: "https://www.tiktok.com/@yesamen.app", label: "TikTok", Icon: TikTokIcon, testId: "nav-social-tiktok" },
];

export const Nav = () => {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const solid = !isHome || scrolled || open;

  const linkClass = (isActive) =>
    `text-[11px] font-medium uppercase tracking-[0.28em] transition-colors duration-300 ${
      solid
        ? "text-[#16233B]/70 hover:text-[#16233B]"
        : "text-white/90 [text-shadow:0_1px_14px_rgba(11,19,32,0.5)] hover:text-white"
    } ${isActive ? "border-b border-current pb-1" : ""}`;

  const iconClass = solid
    ? "text-[#16233B]/70 hover:text-[#16233B]"
    : "text-white/90 [text-shadow:0_1px_14px_rgba(11,19,32,0.5)] hover:text-white";

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
        solid
          ? "border-b border-[#0B1320]/10 bg-[#FBF9F5]/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-10">
        <Link
          to="/"
          data-testid="nav-logo"
          className="font-serif text-lg tracking-[0.18em] text-[#16233B]"
        >
          YES &amp; AMEN
        </Link>

        <nav className="hidden items-center gap-9 md:flex" aria-label="Primary">
          {NAV_LINKS.map(({ to, label, testId }) => (
            <NavLink key={to} to={to} data-testid={testId} className={({ isActive }) => linkClass(isActive)}>
              {label}
            </NavLink>
          ))}
          <div className="flex items-center gap-4">
            {SOCIALS.map(({ href, label, Icon, testId }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={testId}
                className={`transition-colors duration-300 ${iconClass}`}
              >
                <Icon className="h-[17px] w-[17px]" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </nav>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          className={`md:hidden ${solid ? "text-[#16233B]" : "text-[#16233B]"}`}
        >
          {open ? <X className="h-6 w-6" strokeWidth={1.5} /> : <Menu className="h-6 w-6" strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="border-t border-[#0B1320]/10 bg-[#FBF9F5] px-6 py-8 md:hidden"
        >
          <nav className="flex flex-col gap-6" aria-label="Mobile">
            {NAV_LINKS.map(({ to, label, testId }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`${testId}-mobile`}
                className={({ isActive }) =>
                  `text-xs font-medium uppercase tracking-[0.28em] transition-colors duration-300 ${
                    isActive ? "text-[#16233B]" : "text-[#16233B]/60 hover:text-[#16233B]"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
            <div className="mt-2 flex items-center gap-6 border-t border-[#0B1320]/10 pt-6">
              {SOCIALS.map(({ href, label, Icon, testId }) => (
                <a
                  key={label}
                  href={href}
                target="_blank"
                rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`${testId}-mobile`}
                  className="text-[#16233B]/70 transition-colors duration-300 hover:text-[#16233B]"
                >
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
